import React, { useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { getConnectivityErrorMessage } from '../../../util/connectivityErrorUtils';
import {
  wizardCheckConnectivity,
  wizardChange,
  wizardFinishLoading,
  wizardSaveCrawlerSettings,
  wizardStartLoading,
} from '../../../slices/WizardSlice';
import { validateCrawlerUrl } from './connectivityValidation';
import { assertRuntimeSaveSucceeded } from '../../../app/runtime/saveOutcome';

const iconCheckSuccess = require('assets/wizard/icon-catalog-done.svg');

/**
 * Connectivity Check Form Component
 * Reusable form for checking connectivity to shop URL
 */
const ConnectivityCheckForm = forwardRef(({
  autoCheck = false,
  showTitle = true,
  onHasChanges,
  crawlerApi,
}, ref) => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();

  const settings = useSelector((s) => s.settings);
  const { shopAddress, crawler } = settings;
  const {
    stepLoading,
    connectivityStatus,
    connectivityError,
    connectivityChecked,
    connectivityResult,
    crawlerSettings,
  } = useSelector((s) => s.wizard);

  const formData = crawlerSettings;

  // Validate URL helper
  const validateUrl = useCallback(
    url => validateCrawlerUrl(url, localizeMessage),
    [localizeMessage],
  );

  // Expose save and reload methods to parent via ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      // Save without checking connectivity
      const validation = validateUrl(formData.baseUrl);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
      if (crawlerApi) {
        const result = await crawlerApi.save(formData);
        assertRuntimeSaveSucceeded(result, 'Crawler settings were not saved.');
      } else {
        dispatch(wizardSaveCrawlerSettings(formData));
      }
      if (onHasChanges) onHasChanges(false);
    },
    reload: async () => {
      // Reload from settings
      const savedBaseUrl = crawler?.baseUrl;
      const savedAuthType = crawler?.authType || 'none';
      const savedAuthLogin = crawler?.authLogin || '';

      const initialData = {
        baseUrl: savedBaseUrl || '',
        authType: savedAuthType,
        authLogin: savedAuthLogin,
        authPassword: '',
      };

      dispatch(wizardChange({ crawlerSettings: initialData }));
      if (onHasChanges) onHasChanges(false);
    },
  }), [crawlerApi, dispatch, formData, crawler, validateUrl, onHasChanges]);

  // Initialize form data from settings
  useEffect(() => {
    if (crawlerSettings.baseUrl) {
      return;
    }

    const savedBaseUrl = crawler?.baseUrl;
    const savedAuthType = crawler?.authType || 'none';
    const savedAuthLogin = crawler?.authLogin || '';

    let initialData;

    if (savedBaseUrl) {
      initialData = {
        baseUrl: savedBaseUrl,
        authType: savedAuthType,
        authLogin: savedAuthLogin,
        authPassword: '',
      };
    } else if (shopAddress) {
      const initialUrl = shopAddress.startsWith('http') ? shopAddress : `https://${shopAddress}`;
      initialData = {
        baseUrl: initialUrl,
        authType: 'none',
        authLogin: '',
        authPassword: '',
      };
    } else {
      initialData = {
        baseUrl: '',
        authType: 'none',
        authLogin: '',
        authPassword: '',
      };
    }

    dispatch(wizardChange({ crawlerSettings: initialData }));
  }, [crawler, shopAddress, crawlerSettings.baseUrl, dispatch]);

  const handleFieldChange = useCallback((field) => (event) => {
    const { value } = event.target;
    const newFormData = {
      ...crawlerSettings,
      [field]: value,
    };
    dispatch(wizardChange({ crawlerSettings: newFormData }));
    if (onHasChanges) onHasChanges(true);
  }, [crawlerSettings, dispatch, onHasChanges]);


  const handleCheckConnection = useCallback(async () => {
    const validation = validateUrl(formData.baseUrl);
    if (!validation.valid) {
      dispatch(wizardChange({
        connectivityStatus: 'error',
        connectivityError: 'INVALID_URL_FORMAT',
        connectivityChecked: true,
        connectivityResult: { message: validation.error },
      }));
      return;
    }

    dispatch(wizardChange({
      crawlerSettings: formData,
      connectivityChecked: false,
      connectivityStatus: undefined,
      connectivityError: undefined,
      connectivityResult: undefined,
    }));

    // Runtime-backed cloud settings need an awaitable result. The plugin and
    // legacy wizard retain their existing saga path.
    if (!crawlerApi) {
      dispatch(wizardCheckConnectivity());
      return;
    }

    dispatch(wizardStartLoading());
    dispatch(wizardChange({ connectivityStatus: 'checking', connectivityError: undefined }));
    try {
      const result = await crawlerApi.check(formData);
      dispatch(wizardChange({
        connectivityStatus: result?.accessible ? 'success' : 'error',
        connectivityChecked: true,
        connectivityResult: result,
        connectivityError: result?.accessible ? undefined : result?.errorCode || 'UNKNOWN_ERROR',
      }));
    } catch (error) {
      dispatch(wizardChange({
        connectivityStatus: 'error',
        connectivityChecked: true,
        connectivityError: error?.message || 'NETWORK_ERROR',
      }));
    } finally {
      dispatch(wizardFinishLoading());
    }
  }, [crawlerApi, dispatch, formData, validateUrl]);

  // Auto-check through the same product runtime used by the manual button.
  // This prevents the standalone cloud wizard from falling back to wsClient.
  useEffect(() => {
    if (autoCheck && !connectivityChecked && !stepLoading
      && crawlerSettings.baseUrl && !connectivityError) {
      handleCheckConnection();
    }
  }, [
    autoCheck,
    connectivityChecked,
    connectivityError,
    crawlerSettings.baseUrl,
    handleCheckConnection,
    stepLoading,
  ]);

  const renderStatusSection = () => {
    if (connectivityStatus === 'checking') {
      return (
        <Box textAlign="center" py={5}>
          <CircularProgress size={60} />
          <Typography variant="h6" className="mt-3">
            {localizeMessage('wizard.connectivity.status.checking')}
          </Typography>
          <Typography variant="body2" color="textSecondary" className="mt-2">
            {localizeMessage('wizard.connectivity.status.checkingSubtitle')}
          </Typography>
        </Box>
      );
    }

    if (connectivityStatus === 'success') {
      const responseTime = connectivityResult?.responseTime;
      return (
        <Box textAlign="center" py={5}>
          <img src={iconCheckSuccess} alt="" style={{ width: 80, height: 80 }} />
          <Typography variant="h6" className="text-app-primary mt-3">
            {localizeMessage('wizard.connectivity.status.success')}
          </Typography>
          <Typography variant="body2" color="textSecondary" className="mt-2">
            {localizeMessage('wizard.connectivity.status.successSubtitle')}
          </Typography>
          {responseTime && (
            <Typography variant="caption" color="textSecondary" display="block" className="mt-1">
              {localizeMessage('wizard.connectivity.status.responseTime')}
              :
              {' '}
              {responseTime}
              ms
            </Typography>
          )}
        </Box>
      );
    }

    if (connectivityStatus === 'error') {
      const errorCode = connectivityError || connectivityResult?.errorCode;
      const errorMessage = getConnectivityErrorMessage(errorCode, connectivityResult, localizeMessage);

      return (
        <Box py={2}>
          <Alert severity="error" style={{ textAlign: 'left', marginBottom: '20px' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {localizeMessage('wizard.connectivity.status.error')}
            </Typography>
            <Typography variant="body2" style={{ marginTop: '8px' }}>
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box>
      {showTitle && (
        <>
          <Typography variant="h6" gutterBottom>
            {localizeMessage('wizard.connectivity.form.title')}
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            {localizeMessage('wizard.connectivity.subheader')}
          </Typography>
        </>
      )}

      <Box mt={3}>
        <TextField
          id="crawler-base-url"
          fullWidth
          required
          label={localizeMessage('wizard.connectivity.form.baseUrl')}
          value={formData.baseUrl}
          onChange={handleFieldChange('baseUrl')}
          placeholder="https://example.com"
          helperText={localizeMessage('wizard.connectivity.form.baseUrlHelp')}
          disabled={stepLoading}
          variant="outlined"
          margin="normal"
          error={!formData.baseUrl && connectivityStatus === 'error'}
          autoComplete="off"
        />
      </Box>

      <Box mt={3}>
        <TextField
          fullWidth
          select
          label={localizeMessage('wizard.connectivity.form.authType')}
          value={formData.authType}
          onChange={handleFieldChange('authType')}
          disabled={stepLoading}
          variant="outlined"
          margin="normal"
        >
          <MenuItem value="none">
            {localizeMessage('wizard.connectivity.form.authType.none')}
          </MenuItem>
          <MenuItem value="basic">
            {localizeMessage('wizard.connectivity.form.authType.basic')}
          </MenuItem>
        </TextField>
      </Box>

      {formData.authType === 'basic' && (
        <>
          <Box mt={2}>
            <TextField
              fullWidth
              label={localizeMessage('wizard.connectivity.form.authLogin')}
              value={formData.authLogin}
              onChange={handleFieldChange('authLogin')}
              disabled={stepLoading}
              variant="outlined"
              margin="normal"
              autoComplete="off"
            />
          </Box>

          <Box mt={2}>
            <TextField
              fullWidth
              type="password"
              label={localizeMessage('wizard.connectivity.form.authPassword')}
              value={formData.authPassword}
              onChange={handleFieldChange('authPassword')}
              disabled={stepLoading}
              variant="outlined"
              margin="normal"
              autoComplete="new-password"
            />
          </Box>
        </>
      )}

      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCheckConnection}
          disabled={stepLoading || !formData.baseUrl}
          startIcon={stepLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {stepLoading
            ? localizeMessage('wizard.connectivity.form.button.checking')
            : localizeMessage('wizard.connectivity.form.button.checkConnection')}
        </Button>
      </Box>

      {renderStatusSection()}
    </Box>
  );
});

ConnectivityCheckForm.propTypes = {
  autoCheck: PropTypes.bool,
  showTitle: PropTypes.bool,
  onHasChanges: PropTypes.func,
  crawlerApi: PropTypes.shape({
    check: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
  }),
};

ConnectivityCheckForm.defaultProps = {
  autoCheck: false,
  showTitle: true,
  onHasChanges: null,
  crawlerApi: null,
};

export default ConnectivityCheckForm;
